import { Children, Fragment, isValidElement } from 'react'
import type { ReactElement, ReactNode } from 'react'

// MDX v3 compiles content into a _createMdxContent function that returns a
// Fragment containing the top-level slide components. When MDXProvider supplies
// a wrapper component (e.g. SlideDeck), the wrapper receives _createMdxContent
// as a single child element — not the Fragment's children directly.
//
// This helper recursively unwraps both Fragments and MDX content functions so
// each top-level archetype block (Hero, TextLed, etc.) becomes its own slide.
export function flattenChildren(children: ReactNode): ReactNode[] {
  const arr = Children.toArray(children)
  if (arr.length === 1) {
    const only = arr[0]
    if (isValidElement(only)) {
      // Unwrap React Fragments
      if (only.type === Fragment) {
        return flattenChildren((only as ReactElement<{ children?: ReactNode }>).props.children)
      }
      // Unwrap MDX v3 _createMdxContent function component.
      // It returns a Fragment containing the slide archetypes.
      // We call it here (during render) so its internal useContext call works.
      if (typeof only.type === 'function') {
        try {
          const result = (only.type as Function)(only.props)
          if (isValidElement(result) && result.type === Fragment) {
            return flattenChildren((result as ReactElement<{ children?: ReactNode }>).props.children)
          }
        } catch {
          // Fall through — treat as single slide if unwrapping fails
        }
      }
    }
  }
  return arr.filter((child) => {
    if (typeof child === 'string') return child.trim().length > 0
    return isValidElement(child)
  })
}
